import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import ROUTES, { SWITCH_ROUTES } from '@constants/routes';
import { useGlobalProps } from '@hooks/useGlobalProps';
import { useTranslation } from 'react-i18next';

export default function PWAInstallPage() {
    const navigate = useNavigate();
    const { freePlayEnabled } = useGlobalProps();
    const { t } = useTranslation();
    
    // Get translation objects
    const tCommon = t('common', { returnObjects: true });
    const tInstall = t('install', { returnObjects: true });

    // Track if install prompt is available
    useEffect(() => {
        // Check if install prompt was already captured in index.html
        if (window.installPrompt) {
            console.log('Using install prompt from index.html', window.installPrompt);
            window.deferredPrompt = window.installPrompt;
        } else {
            // If not captured yet, listen for it
            const handleBeforeInstallPrompt = (e) => {
                e.preventDefault();
                window.deferredPrompt = e;
                window.installPrompt = e;
                console.log('Captured install prompt in PWAInstallPage', e);
            };

            console.log('Adding beforeinstallprompt listener');
            window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

            return () => {
                window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            };
        }
    }, []);

    const handleInstallPWA = async () => {
        // This function is now only called by Android devices (due to UI changes)
        const installPrompt = window.deferredPrompt || window.installPrompt;
        
        if (installPrompt) {
            try {
                console.log('Showing install prompt for Android device');
                await installPrompt.prompt();
                
                const choiceResult = await installPrompt.userChoice;
                
                if (choiceResult.outcome === 'accepted') {
                    console.log('User accepted the install prompt');
                    // Navigate to login after successful installation
                    navigate(SWITCH_ROUTES.LOGIN);
                } else {
                    console.log('User dismissed the install prompt');
                    // Still navigate to login even if dismissed
                    navigate(SWITCH_ROUTES.LOGIN);
                }
                
                // Clean up the prompt references
                window.deferredPrompt = null;
                window.installPrompt = null;
                
            } catch (error) {
                console.error('Error during PWA installation:', error);
                // On error, still navigate to login to not block user flow
                navigate(SWITCH_ROUTES.LOGIN);
                
                // Clean up on error too
                window.deferredPrompt = null;
                window.installPrompt = null;
            }
        } else {
            console.log('No install prompt available for Android device, navigating to login');
            // If no install prompt available, go directly to login
            navigate(SWITCH_ROUTES.LOGIN);
        }
    };

    const handleHelp = () => {
        // Determine OS parameter for help video URL
        let osParam = 'x'; // Default to 'x' for other platforms
        if (isIOS) {
            osParam = 'i'; // iOS
        } else if (isAndroid) {
            osParam = 'a'; // Android
        }
        
        // Open help video URL with appropriate OS parameter
        const helpVideoUrl = `https://tt.ace23.cc/video/userInstallHelp.ashx?os=${osParam}`;
        window.open(helpVideoUrl, '_blank');
    };

    const handleFreePlay = () => {
        // Navigate to free play/game interface - trial version
        navigate(ROUTES.GAME + '?mode=trial');
    };

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);

    // For desktop, we should automatically redirect to Screen B (Login Flow)
    useEffect(() => {
        const isMobile = isIOS || isAndroid;
        if (!isMobile) {
            // If desktop, redirect to Screen B (Login Flow)
            navigate(SWITCH_ROUTES.LOGIN);
        }
    }, [isIOS, isAndroid, navigate]);
    
    return (
        <Container>
            <LogoContainer>
                <Logo src="/images/new_logo.png" alt="Logo" />
            </LogoContainer>
            <ButtonsContainer>
                {isAndroid && (
                    <ActionButton primary onClick={handleInstallPWA}>
                        <ButtonIcon>📲</ButtonIcon>
                        {tInstall['install@install-app'] || 'Install'}
                    </ActionButton>
                )}
                
                {isIOS && (
                    <ActionButton onClick={handleHelp}>
                        <ButtonIcon>🎥</ButtonIcon>
                        {tInstall['install@help'] || 'Help'}
                    </ActionButton>
                )}
                
                {/* {freePlayEnabled && (
                    <ActionButton onClick={handleFreePlay}>
                        <ButtonIcon>🎮</ButtonIcon>
                        {tInstall['install@free-play'] || 'Free Play'}
                    </ActionButton>
                )} */}
            </ButtonsContainer>
        </Container>
    );
}

const Container = styled.div`
    min-height: 100vh;
    width: 100vw;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 0;
    background: url('/images/new_background.jpg') center center / cover no-repeat,
                radial-gradient(ellipse at center, #1e3c72 0%, #2a5298 50%, #0f1419 100%);
    position: relative;
    
    /* Adjust padding on mobile */
    @media (max-width: 768px) {
        padding: 20px 0;
    }
`;

const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    max-width: 300px;
    padding: 40px 20px;
    margin-bottom: 60px;
    
    /* Adjust spacing on mobile */
    @media (max-width: 768px) {
        padding: 30px 20px;
        margin-bottom: 40px;
    }
    
    @media (max-width: 480px) {
        padding: 20px 15px;
        margin-bottom: 30px;
        gap: 15px;
    }
`;

const ActionButton = styled.button`
    background: transparent;
    color: white;
    border: none;
    padding: 18px 30px;
    border-radius: 12px;
    font-size: 2rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    
    &:hover {
        transform: translateY(-2px);
        opacity: 0.8;
    }
    
    &:active {
        transform: translateY(0);
        opacity: 0.6;
    }
    
    /* Responsive font size and padding */
    @media (max-width: 768px) {
        font-size: 1.8rem;
        padding: 16px 25px;
    }
    
    @media (max-width: 480px) {
        font-size: 1.6rem;
        padding: 14px 20px;
        gap: 10px;
    }
`;

const ButtonIcon = styled.span`
    font-size: 1.8rem;
    
    @media (max-width: 768px) {
        font-size: 1.6rem;
    }
    
    @media (max-width: 480px) {
        font-size: 1.4rem;
    }
`;

const LogoContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    
    /* Reduce padding on mobile */
    @media (max-width: 768px) {
        padding: 15px;
    }
    
    @media (max-width: 480px) {
        padding: 10px;
    }
`;

const Logo = styled.img`
    max-width: 450px;
    max-height: 300px;
    object-fit: contain;
    
    /* Mobile responsive styles */
    @media (max-width: 768px) {
        max-width: 250px;
        max-height: 150px;
    }
    
    /* Small mobile devices */
    @media (max-width: 480px) {
        max-width: 300px;
        max-height: 200px;
    }
`;